import { INITIAL_PAGE, INITIAL_TABLE_PAGE_SIZE } from "@/shared/common/constants";
import { debounce } from "@/shared/utils";
import type { TTable } from "@ab.uitools/ui-library/components/AdvancedTable/types";
import { useCallback, useRef, useState } from "react";

export const useTableHooks = <T>() => {
  const tableRef = useRef<TTable<T>>();
  const [pageSize, setPageSize] = useState<number>(INITIAL_TABLE_PAGE_SIZE);
  const [page, setPage] = useState<number>(INITIAL_PAGE);
  const [debouncedPage, setDebouncedPage] = useState<number>(INITIAL_PAGE);
  const [hasNextPage, setHasNextPage] = useState<boolean>(true);

  const updateDebouncedPage = useCallback(
    debounce((nextPage: number) => {
      setDebouncedPage(nextPage);
    }, 400),
    []
  );

  const onNextPage = () => {
    setPage((prev) => {
      const next = prev + 1;
      tableRef.current?.nextPage();
      updateDebouncedPage(next);
      return next;
    });
  };

  const onPrevPage = () => {
    if (page > INITIAL_PAGE) {
      setPage((prev) => {
        const next = prev - 1;
        tableRef.current?.previousPage();
        updateDebouncedPage(next);
        return next;
      });
      if (!hasNextPage) {
        setHasNextPage(true);
      }
    }
  };

  const onPageIndexReset = () => {
    updateDebouncedPage.cancel();
    setPage(INITIAL_PAGE);
    setDebouncedPage(INITIAL_PAGE);
    setHasNextPage(true);
  };

  const onPageSizeChange = (value?: string | number | null) => {
    if (value) {
      updateDebouncedPage.cancel();
      setPageSize(Number(value));
      setPage(INITIAL_PAGE);
      setDebouncedPage(INITIAL_PAGE);
      setHasNextPage(true);

      tableRef.current?.setPagination({
        pageIndex: 0,
        pageSize: Number(value),
      });
    }
  };

  const updateHasNextPage = (rowsCount: number) => {
    setHasNextPage(rowsCount >= pageSize);
  };

  return {
    page: debouncedPage,
    pageSize,
    tableRef,
    hasNextPage,
    setHasNextPage,
    updateHasNextPage,
    onNextPage,
    onPrevPage,
    onPageSizeChange,
    onPageIndexReset,
  };
};
